import { isUploadInProgress } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/internal/fileAttachmentProjection.js';
import { isRejectedStoredUpload, mergeUploadChanges, } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/internal/fileAttachmentUpdates.js';
import { ATTACHMENT_FAILURE_MESSAGES } from '#pplx/frontend/components/utils/attachmentsUtils.js';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
export function useFileAttachmentUpload({ activeControllersRef, disabled, files, gateUploadFile, onUploadAttempted, rejectFiles, removedIdsRef, setError, setFiles, setAttachmentMetadataById, uploadFiles, uploadPolicy, }) {
    const { $t } = useIntl();
    return useCallback((selectedFiles, source, metadata) => {
        if (disabled || selectedFiles.length === 0)
            return;
        if (files.length + selectedFiles.length > uploadPolicy.constraints.maxFiles) {
            onUploadAttempted?.({ fileCount: selectedFiles.length, gated: false, source });
            rejectFiles(selectedFiles, 'too_many_files');
            setError({
                type: 'selection',
                message: $t(ATTACHMENT_FAILURE_MESSAGES.over_file_count, {
                    maxNumFiles: uploadPolicy.constraints.maxFiles,
                }),
            });
            return;
        }
        const gateError = gateUploadFile();
        const gated = Boolean(gateError);
        onUploadAttempted?.({ fileCount: selectedFiles.length, gated, source });
        if (gateError) {
            rejectFiles(selectedFiles, gateError);
            return;
        }
        setError(undefined);
        const controller = new AbortController();
        activeControllersRef.current.add(controller);
        const batchFileIds = new Set();
        let metadataFileId;
        void uploadFiles([...selectedFiles], (changedFiles) => {
            changedFiles.forEach((file) => batchFileIds.add(file.id));
            if (metadata !== undefined && metadataFileId === undefined) {
                // Only uploadFileAsAttachment supplies metadata, for a single-file batch.
                const trackedFile = changedFiles[0];
                if (trackedFile !== undefined) {
                    metadataFileId = trackedFile.id;
                    setAttachmentMetadataById((current) => new Map(current).set(trackedFile.id, metadata));
                }
            }
            const rejectedFiles = changedFiles.filter((file) => !removedIdsRef.current.has(file.id) && isRejectedStoredUpload(file));
            const rejectedIds = new Set(rejectedFiles.map((file) => file.id));
            if (rejectedIds.size > 0) {
                rejectedIds.forEach((id) => removedIdsRef.current.add(id));
                setAttachmentMetadataById((current) => {
                    const next = new Map(current);
                    rejectedIds.forEach((id) => next.delete(id));
                    return next;
                });
                setError({
                    type: 'upload',
                    // useUploadFiles localizes processing errors before publishing the rejected file.
                    message: rejectedFiles[0]?.error ?? $t(ATTACHMENT_FAILURE_MESSAGES.generic_upload_error),
                });
            }
            setFiles((current) => mergeUploadChanges({
                changedFiles: changedFiles.filter((file) => !rejectedIds.has(file.id)),
                current: rejectedIds.size === 0
                    ? current
                    : current.filter((file) => !rejectedIds.has(file.id)),
                removedIds: removedIdsRef.current,
            }));
        }, controller.signal)
            .catch(() => {
            if (controller.signal.aborted)
                return;
            const error = $t(ATTACHMENT_FAILURE_MESSAGES.generic_upload_error);
            setFiles((current) => current.map((file) => batchFileIds.has(file.id) && isUploadInProgress(file)
                ? { ...file, status: 'failed', error }
                : file));
        })
            .finally(() => activeControllersRef.current.delete(controller));
    }, [
        $t,
        activeControllersRef,
        disabled,
        files.length,
        gateUploadFile,
        onUploadAttempted,
        rejectFiles,
        removedIdsRef,
        setError,
        setFiles,
        setAttachmentMetadataById,
        uploadFiles,
        uploadPolicy.constraints.maxFiles,
    ]);
}
//# sourceMappingURL=useFileAttachmentUpload.js.map